import { useEffect, useState } from "react";
import { ReferenceDropzone } from "../features/reference-load/ReferenceDropzone";
import { AboutButton } from "../features/about/AboutButton";
import { AxonResourcesCard } from "../features/brand/AxonResourcesCard";
import { Button } from "../design-system/primitives/Button";
import { useNbStore } from "../store/nbStore";
import { usePagesStore } from "../store/pagesStore";
import { parseSignalList } from "../lib/referenceParsers";
import { listScl, suggestIed } from "../lib/sclMapper";
import { BRANDS_MAPEABLES } from "../lib/genericNodes";
import { Modal } from "../design-system/primitives/Modal";
import { useLangStore } from "../store/langStore";
import { t as tr } from "../lib/i18n";

const IED_OPTIONS = [
  "7SL82",
  "7SL86",
  "7SL87",
  "7SA82",
  "7SA86",
  "7SA87",
  "7UT82",
  "7UT85",
  "7SK82",
  "7SK85",
  "7UM85",
  "7SJ81",
  "7SJ82",
  "7SJ85",
  "REF620",
  "REM620",
  "RET620",
  "RER620",
  "REF615",
  "REM615",
  "RET615",
  "RED615",
  "REC615",
  "REX640",
  "REL670",
  "RED670",
  "RET670",
  "REG670",
  "REC670",
  "SEL411L",
  "SEL421",
  "SEL700BT",
  "SEL700G",
  "SEL751",
  "SEL787L",
  "SEL451",
  "SEL487B",
  "SEL487E",
  "Familia UR"
];

type Mode = "assign" | "map" | "validate";

const MODES: { id: Mode; label: string; hint: string }[] = [
  { id: "assign",   label: "Asignar nodo MMS", hint: "Entra al espacio de trabajo directamente, selecciona el IED, Añade señales desde un archivo, manualmente o selecciona señales típicas, el aplicativo te indicará el nodo IEC 61850 respectivo. Al finalizar podrás descargar el listado de señales." },
  { id: "map",      label: "Mapear a SCL",     hint: "Carga un Excel/CSV de señales con su respectivo nodo lógico IEC 61850 y dataset donde quieres alojarlo (en tres columnas \"Nombre Señal\", \"Nodo MMS\", \"Dataset\") y un archivo SCL (.scd/.cid). El aplicativo procederá a mapear el nodo lógico en el archivo SCL." },
  { id: "validate", label: "Validar en SCL",   hint: "Carga un Excel/CSV de señales con su respectivo nodo lógico IEC 61850 (en dos columnas \"Nombre Señal\" y \"Nodo MMS\") y un archivo SCL (.scd/.cid). Se validará la presencia de cada nodo." },
];

export function LandingPage() {
  const { initModels, modelsLoaded, runInference, runValidation } = useNbStore();
  const [mode,         setMode]          = useState<Mode>("assign");
  const [iedRef,       setIedRef]        = useState("");
  const [localError,   setLocalError]    = useState("");
  const [busy,         setBusy]          = useState(false);
  const [uploadedFiles,setUploadedFiles] = useState<File[]>([]);
  const [fileNames,    setFileNames]     = useState<string[]>([]);
  const [mapBrand,     setMapBrand]      = useState<string>("");   // marca obligatoria en modo "map"
  const [mapPicker,    setMapPicker]     = useState<null | {
    rows: { signal: string; mmsNode: string; iedRef: string; dsTarget?: string }[];
    sclText: string; fileName: string;
    ieds: { name: string; lDevices: string[] }[]; datasets: string[]; chosen: string;
    brand: string;
  }>(null);

  useEffect(() => { void initModels(); }, [initModels]);

  // Al cambiar de modo, limpiar archivos y errores para no arrastrar estado confuso
  const switchMode = (m: Mode) => {
    setMode(m);
    setLocalError("");
    setUploadedFiles([]);
    setFileNames([]);
  };

  const handleFiles = (files: File[]) => {
    setUploadedFiles(prev => {
      const incoming = [...files];
      const isScl  = (f: File) => /\.(scd|cid|icd|iid|scl|xml)$/i.test(f.name);
      const isXlsx = (f: File) => /\.(xlsx|xls|csv|txt)$/i.test(f.name);
      const retained = prev.filter(p =>
        (incoming.some(isScl)  && isXlsx(p)) ||
        (incoming.some(isXlsx) && isScl(p))
      );
      const merged = [...retained, ...incoming];
      setFileNames(merged.map(f => f.name));
      return merged;
    });
    setLocalError("");
  };
  const getXlsx = () => uploadedFiles.find(f => /\.(xlsx|xls|csv|txt)$/i.test(f.name));
  const getScl  = () => uploadedFiles.find(f => /\.(scd|cid|icd|iid|scl|xml)$/i.test(f.name));
  const readBytes = (f: File) => f.arrayBuffer().then(b => new Uint8Array(b));

  // ── Modo "Asignar nodo MMS": entra al workspace; archivo e IED opcionales ────
  // Con IED + archivo: clasifica de inmediato. Con IED sin archivo: entra vacío
  // para añadir señales/típicos dentro. Sin IED: entra y el IED se elige dentro.
  const runAssign = async () => {
    setLocalError("");
    const xlsxFile = getXlsx();
    const id = `tab-${Date.now()}`;
    if (!xlsxFile) {
      // Sin archivo: entrar directo (equivale al antiguo "crear desde cero")
      if (iedRef) useNbStore.getState().setTab(id, { selectedIed: iedRef });
      const title = iedRef ? `${tr("Señales —",lang)} ${iedRef}` : tr("Nuevo listado",lang);
      usePagesStore.getState().open({ id, title, kind: "results" });
      return;
    }
    if (!modelsLoaded) { setLocalError(tr("El motor aún se está cargando. Espera un momento.",lang)); return; }
    setBusy(true);
    try {
      const parsed = await parseSignalList(await readBytes(xlsxFile), xlsxFile.name);
      if (iedRef) {
        await runInference(id, parsed.map(p => p.signal), iedRef);
        usePagesStore.getState().open({ id, title: `Señales — ${iedRef}`, kind: "results" });
      } else {
        const rows = parsed.map(p => ({ signal: p.signal, mmsNode: p.mmsNode ?? "—", iedRef: "" }));
        useNbStore.getState().setTab(id, { rows, status: "ready" });
        usePagesStore.getState().open({ id, title: tr("Señales (sin IED)",lang), kind: "results" });
      }
    } finally { setBusy(false); }
  };

  // ── Modo "Mapear a SCL": Excel (Señal/Nodo/Dataset) + SCL → elegir IED → workspace ──
  const runMap = async () => {
    setLocalError("");
    const xlsxFile = getXlsx(), sclFile = getScl();
    if (!mapBrand) { setLocalError(tr("Selecciona la marca del IED para mapear.",lang)); return; }
    if (!xlsxFile) { setLocalError(tr("Carga un Excel/CSV con señal, nodo MMS y dataset.",lang)); return; }
    if (!sclFile)  { setLocalError(tr("Carga también el archivo SCL (.scd/.cid).",lang)); return; }
    setBusy(true);
    try {
      const parsed = await parseSignalList(await readBytes(xlsxFile), xlsxFile.name);
      const sclText = new TextDecoder().decode(await readBytes(sclFile));
      const inv = listScl(sclText);
      if (!inv.ieds.length) { setLocalError(tr("El SCL no contiene elementos <IED>",lang)); setBusy(false); return; }
      const rows = parsed.map(p => ({
        signal:  p.signal,
        mmsNode: p.mmsNode ?? "—",
        iedRef:  "",
        dsTarget: p.dataset,
      }));
      // Preseleccionar IED por similitud de la primera señal con nodo
      const primera = rows.find(r => r.mmsNode && r.mmsNode !== "—");
      const iedSug  = (primera ? suggestIed(primera.mmsNode, inv.ieds) : inv.ieds[0].name) ?? inv.ieds[0].name;
      // Abrir ventana emergente de selección de IED (igual que "Cargar SCL")
      setMapPicker({ rows, sclText, fileName: sclFile.name, ieds: inv.ieds,
                     datasets: inv.datasets, chosen: iedSug, brand: mapBrand });
    } finally { setBusy(false); }
  };
  const confirmMapPicker = () => {
    if (!mapPicker) return;
    const id = `tab-${Date.now()}`;
    useNbStore.getState().setTab(id, {
      rows: mapPicker.rows, status: "ready",
      mapSclText: mapPicker.sclText, mapSclFileName: mapPicker.fileName,
      mapIeds: mapPicker.ieds, mapDatasets: mapPicker.datasets, mapIedTarget: mapPicker.chosen,
      mapBrandLock: mapPicker.brand,   // habilita el mapeo sin exigir referencia
      hintMapButton: true,   // dispara la flecha temporal hacia "Mapear nodos a SCL"
    });
    usePagesStore.getState().open({ id, title: tr("Mapear —",lang) + " " + mapPicker.fileName, kind: "results" });
    setMapPicker(null);
  };

  // ── Modo "Validar en SCL": valida señales contra un SCL ─────────────────────
  const runValidateScl = async () => {
    setLocalError("");
    if (!modelsLoaded) { setLocalError(tr("El motor aún se está cargando. Espera un momento.",lang)); return; }
    const xlsxFile = getXlsx(), sclFile = getScl();
    if (!xlsxFile) { setLocalError(tr("Carga un Excel/CSV con señal y nodo MMS.",lang)); return; }
    if (!sclFile)  { setLocalError(tr("Carga también el archivo SCL (.scd/.cid).",lang)); return; }
    setBusy(true);
    try {
      const id = `tab-${Date.now()}`;
      await runValidation(id, await readBytes(xlsxFile), xlsxFile.name, await readBytes(sclFile), sclFile.name);
      usePagesStore.getState().open({ id, title: tr("Validación SCL",lang), kind: "results" });
    } finally { setBusy(false); }
  };

  const { lang, toggleLang } = useLangStore();
  const needsIed = mode === "assign";
  const showDropzone = mode === "map" || mode === "validate";

  return (
    <div className="relative h-full overflow-y-auto">
      <header className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
        <AboutButton />
        <button onClick={toggleLang}
        title={lang==="es"?"Switch interface to English":"Cambiar interfaz a español"}
        className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-bold tracking-widest text-[var(--color-on-surface)] hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]">
        {lang.toUpperCase()}
      </button>
      </header>
      <div className="flex min-h-full flex-col items-center justify-center gap-8 p-8">
      <div className="text-center">
        <h1 className="text-3xl font-semibold text-[var(--color-brand)]">Axon Tags</h1>
        <p className="mt-1 text-sm text-[var(--color-on-surface-variant)]">
          {tr("Asistente IA para asignación automática, mapeo y validación de nodos MMS IEC 61850",lang)}
        </p>
      </div>

      <div className="w-full max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-container)] overflow-hidden">
        {/* ── Pestañas de modo ── */}
        <div className="flex border-b border-[var(--color-border)]">
          {MODES.map(m => (
            <button key={m.id} onClick={() => switchMode(m.id)}
              className={"flex-1 px-3 py-2.5 text-xs font-medium transition-colors " +
                (mode === m.id
                  ? "border-b-2 border-[var(--color-brand)] text-[var(--color-brand)] bg-[var(--color-brand-soft-10)]"
                  : "text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-variant)]")}>
              {tr(m.label,lang)}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {/* Descripción del modo activo */}
          <p className="text-xs text-[var(--color-on-surface-variant)] leading-relaxed">
            {tr(MODES.find(m => m.id === mode)!.hint,lang)}
          </p>

          {/* Marca — obligatoria, solo en 'Mapear a SCL' */}
          {mode === "map" && (
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-[var(--color-on-surface-variant)]">
                {tr("Marca del IED",lang)} <span className="normal-case opacity-60">({tr("obligatorio",lang)})</span>
              </label>
              <select value={mapBrand} onChange={(e)=>setMapBrand(e.target.value)}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 font-mono text-sm outline-none focus:border-[var(--color-brand)]">
                <option value="">{tr("— Selecciona una marca —",lang)}</option>
                {BRANDS_MAPEABLES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}

          {/* IED — opcional, solo en 'Asigna nodos MMS' */}
          {needsIed && (
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-[var(--color-on-surface-variant)]">
                Referencia del IED <span className="normal-case opacity-60">({tr("Opcional, el IED también lo puedes elegir dentro",lang)})</span>
              </label>
              <input
                type="text"
                list="ied-options-list"
                placeholder={tr("Escribe o selecciona (ej: REF620, 7SJ81…)",lang)}
                value={iedRef}
                onChange={e => {
                  const v = e.target.value;
                  const match = IED_OPTIONS.find(o => o.toLowerCase() === v.toLowerCase().trim());
                  setIedRef(match ?? v);
                }}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
              />
              <datalist id="ied-options-list">
                {IED_OPTIONS.map(ied => <option key={ied} value={ied} />)}
              </datalist>
            </div>
          )}

          {/* Dropzone — solo en modos con archivo */}
          {showDropzone && (
            <>
              <ReferenceDropzone onFiles={handleFiles}
                label={mode === "validate"
                  ? tr("Arrastra el Excel/CSV de señales y el SCL (.scd/.cid)",lang)
                  : tr("Arrastra el Excel/CSV (Señal, Nodo MMS, Dataset) y el SCL (.scd/.cid)",lang)} />
              {fileNames.length > 0 && (
                <ul className="list-disc pl-4 text-xs text-[var(--color-on-surface-variant)]">
                  {fileNames.map(n => <li key={n}>{n}</li>)}
                </ul>
              )}
            </>
          )}

          {/* Acción principal — una sola, con nombre claro por modo */}
          <div className="flex justify-center pt-1">
            {mode === "assign" && (
              <Button variant="primary" onClick={() => void runAssign()} disabled={busy}>
                {busy ? tr("Cargando…",lang) : (getXlsx() ? tr("Cargar y entrar",lang) : tr("Entrar al listado",lang))}
              </Button>
            )}
            {mode === "map" && (
              <Button variant="primary" onClick={() => void runMap()} disabled={busy || !mapBrand}
                      title={!mapBrand ? tr("Selecciona la marca del IED para mapear.",lang) : ""}>
                {busy ? tr("Cargando…",lang) : tr("Cargar para mapear",lang)}
              </Button>
            )}
            {mode === "validate" && (
              <Button variant="primary" onClick={() => void runValidateScl()} disabled={busy}>
                {busy ? tr("Validando…",lang) : tr("Validar en SCL",lang)}
              </Button>
            )}
          </div>

          {localError && (
            <p className="text-center text-xs text-[var(--color-status-absent)]">⚠ {localError}</p>
          )}
        </div>
      </div>
      <p className="mx-auto -mt-4 max-w-lg text-center text-xs leading-relaxed text-[var(--color-on-surface-variant)] opacity-70">
        🔒 {tr("Los archivos se procesan localmente en tu navegador. La información nunca se sube a un servidor ni queda visible para Axon Group.",lang)}
      </p>
      <AxonResourcesCard />
      </div>

      {/* ── Ventana emergente: selección de IED destino (modo Mapear a SCL) ── */}
      <Modal open={!!mapPicker} onClose={() => setMapPicker(null)}
             ariaLabel={tr("Selecciona el IED destino",lang)} widthClass="w-[min(420px,92vw)]">
        {mapPicker && (
          <div className="flex flex-col gap-4 p-6">
            <h2 className="pr-8 text-base font-semibold text-[var(--color-on-surface)]">
              {tr("Selecciona el IED destino",lang)}
            </h2>
            <p className="text-xs leading-relaxed text-[var(--color-on-surface-variant)]">
              {tr("Los nodos de esta pestaña se mapearán en los DataSets MMS del IED que elijas.",lang)}
              <span className="mt-1 block font-mono text-[10px] opacity-70">{mapPicker.fileName}</span>
            </p>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">IED</label>
              <select value={mapPicker.chosen}
                onChange={(e) => setMapPicker({ ...mapPicker, chosen: e.target.value })}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 font-mono text-sm outline-none focus:border-[var(--color-brand)]">
                {mapPicker.ieds.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
              </select>
              {(() => {
                const sel = mapPicker.ieds.find(i => i.name === mapPicker.chosen);
                return sel && sel.lDevices.length ? (
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-[var(--color-on-surface-variant)] opacity-70">
                    LDevices: {sel.lDevices.slice(0, 8).join(" · ")}{sel.lDevices.length > 8 ? " …" : ""}
                  </p>
                ) : null;
              })()}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setMapPicker(null)} className="py-1.5 px-3 text-xs">
                {tr("Cancelar",lang)}
              </Button>
              <Button variant="primary" onClick={confirmMapPicker} className="py-1.5 px-3 text-xs">
                {tr("Confirmar",lang)}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
